import {
  applyMapping,
  IMPORT_LIMITS,
  IMPORT_TARGETS,
  type ImportContactRaw,
  type ImportParseResult,
  type ImportPreviewRow,
  type ImportTarget,
} from "@kontaktfix/shared";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  cn,
  Select,
  Spinner,
  Table,
  Td,
  Th,
  Tr,
} from "@kontaktfix/ui";
import {
  CheckCircleIcon,
  CloudArrowUpIcon,
  CopyIcon,
  FileXlsIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { LIST_COLOR_HEX } from "@/components/list-badge";
import { Page, PageHeader } from "@/components/page";
import { api } from "@/lib/api";
import { toastError } from "@/lib/errors";
import { formatNumber } from "@/lib/i18n";
import { useCanChangeContacts, useInvalidateData, useLists } from "@/lib/queries";

type Step = "upload" | "map" | "review";
type RowAction = "create" | "update" | "skip";

function StepIndicator({ step }: { step: Step }) {
  const { t } = useTranslation();
  const steps: Step[] = ["upload", "map", "review"];
  const current = steps.indexOf(step);
  return (
    <div className="mb-6 flex items-center gap-2 text-[12.5px]">
      {steps.map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <span
            className={cn(
              "flex size-5 items-center justify-center rounded-full text-[11px] font-semibold",
              i <= current ? "bg-accent text-accent-fg" : "bg-muted text-fg-muted",
            )}
          >
            {i + 1}
          </span>
          <span className={i === current ? "font-medium" : "text-fg-muted"}>{t(`import.steps.${s}`)}</span>
          {i < steps.length - 1 && <span className="mx-1 h-px w-8 bg-border" />}
        </div>
      ))}
    </div>
  );
}

export function targetLabel(t: (k: string) => string, target: ImportTarget): string {
  if (target === "fullName") return t("import.fullName");
  if (target.startsWith("business") || target.startsWith("home")) {
    const key = `import.fields.${target}`;
    const label = t(key);
    if (label !== key) return label;
  }
  return t(`contacts.fields.${target}`);
}

export function ImportPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const invalidate = useInvalidateData();
  const { data: lists = [] } = useLists();
  const editableLists = lists.filter((l) => l.canEdit);
  const canChange = useCanChangeContacts();
  const fileInput = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>("upload");
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [table, setTable] = useState<Extract<ImportParseResult, { kind: "table" }> | null>(null);
  const [mapping, setMapping] = useState<Record<string, ImportTarget | null>>({});
  const [raw, setRaw] = useState<ImportContactRaw[]>([]);
  const [preview, setPreview] = useState<ImportPreviewRow[]>([]);
  const [actions, setActions] = useState<Record<number, RowAction>>({});
  const [targetLists, setTargetLists] = useState<string[]>([]);
  /** Chunked commit: rows already saved (a retry continues after them) and the total. */
  const [progress, setProgress] = useState<{
    key: string;
    done: number;
    total: number;
    importId: string;
  } | null>(null);

  const upload = async (file: File) => {
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const result = await api.post<ImportParseResult>("/api/import/parse", form);
      if (result.kind === "table") {
        setTable(result);
        setMapping(result.suggestedMapping);
        setStep("map");
      } else {
        await review(result.contacts);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const review = async (contacts: ImportContactRaw[]) => {
    setBusy(true);
    try {
      setRaw(contacts);
      const rows = await api.post<ImportPreviewRow[]>("/api/import/preview", {
        contacts: contacts.map(({ photoBase64: _p, ...c }) => c),
      });
      setPreview(rows);
      const defaults: Record<number, RowAction> = {};
      for (const row of rows) {
        if (row.errors.length || row.duplicateInFile !== null) defaults[row.index] = "skip";
        else if (row.duplicates.some((d) => d.reason === "email")) defaults[row.index] = "update";
        else if (row.duplicates.length) defaults[row.index] = "skip";
        else defaults[row.index] = "create";
      }
      setActions(defaults);
      setTargetLists(editableLists.length === 1 ? [editableLists[0]!.id] : []);
      setStep("review");
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const toImport = preview.filter((r) => r.data && actions[r.index] !== "skip");
  // A failed chunked import resumes only for the same rows, actions and lists.
  const selectionKey = JSON.stringify([toImport.map((r) => [r.index, actions[r.index]]), targetLists]);
  const resumable = progress?.key === selectionKey ? progress : null;
  const counts = useMemo(
    () => ({
      valid: preview.filter((r) => r.data).length,
      invalid: preview.filter((r) => !r.data).length,
      duplicates: preview.filter((r) => r.duplicates.length || r.duplicateInFile !== null).length,
    }),
    [preview],
  );

  /**
   * Saves in chunks (IMPORT_LIMITS), so 25,000 rows with photos stay below the API body limit and
   * the progress is visible. After a failed chunk, the next click continues where it stopped.
   */
  const commit = async () => {
    setBusy(true);
    const rows = toImport.map((r) => ({
      contact: raw[r.index] as Record<string, unknown>,
      action: actions[r.index] === "update" ? ("update" as const) : ("create" as const),
      targetId: actions[r.index] === "update" ? r.duplicates[0]?.contactId : undefined,
    }));
    const key = selectionKey;
    const run =
      progress?.key === key ? progress : { key, done: 0, total: rows.length, importId: crypto.randomUUID() };
    setProgress(run);
    const result = { created: 0, updated: 0 };
    try {
      let done = run.done;
      let chunk = Math.ceil(done / IMPORT_LIMITS.commitChunkRows);
      while (done < rows.length) {
        const part: typeof rows = [];
        let bytes = 0;
        for (const row of rows.slice(done)) {
          const size = JSON.stringify(row).length;
          if (
            part.length &&
            (part.length >= IMPORT_LIMITS.commitChunkRows || bytes + size > IMPORT_LIMITS.commitChunkBytes)
          )
            break;
          part.push(row);
          bytes += size;
        }
        const saved = await api.post<{ created: number; updated: number }>("/api/import/commit", {
          rows: part,
          listIds: targetLists,
          importId: run.importId,
          chunk,
        });
        result.created += saved.created;
        result.updated += saved.updated;
        done += part.length;
        chunk++;
        setProgress({ ...run, done });
      }
      setProgress(null);
      toast.success(t("import.done", result));
      await invalidate();
      await navigate({ to: "/contacts", search: targetLists.length === 1 ? { listId: targetLists[0] } : {} });
    } catch (err) {
      toastError(err);
      // Saved chunks stay saved; the next click continues with the rest.
      if (result.created || result.updated) {
        await invalidate();
        toast.message(t("import.partial", { done: formatNumber(result.created + result.updated) }));
      }
    } finally {
      setBusy(false);
    }
  };

  if (!canChange) {
    return (
      <Page wide>
        <PageHeader title={t("import.title")} description={t("import.description")} />
        <div className="rounded-md border border-border bg-subtle px-4 py-3 text-[13px] text-fg-muted">
          {t("billing.readOnly")}
        </div>
      </Page>
    );
  }

  return (
    <Page wide>
      <PageHeader title={t("import.title")} description={t("import.description")} />
      <StepIndicator step={step} />

      {step === "upload" && (
        <Card
          className={cn(
            "flex cursor-pointer flex-col items-center justify-center border-dashed px-6 py-16 text-center transition-colors",
            dragging ? "border-accent bg-accent-soft/40" : "hover:bg-subtle",
          )}
          onClick={() => fileInput.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            const file = e.dataTransfer.files[0];
            if (file) void upload(file);
          }}
        >
          <div className="mb-4 flex size-12 items-center justify-center rounded-xl border border-border bg-bg text-fg-muted shadow-sm">
            {busy ? <Spinner className="size-5" /> : <CloudArrowUpIcon className="size-6" />}
          </div>
          <div className="text-[14px] font-medium">{busy ? t("import.parsing") : t("import.drop")}</div>
          <div className="mt-1 text-[12.5px] text-fg-muted">{t("import.formats")}</div>
          <input
            ref={fileInput}
            type="file"
            accept=".csv,.txt,.xlsx,.vcf,.vcard"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void upload(file);
              e.target.value = "";
            }}
          />
        </Card>
      )}

      {step === "map" && table && (
        <Card>
          <CardHeader
            title={t("import.mapTitle")}
            description={t("import.mapText")}
            actions={
              <>
                <Button onClick={() => setStep("upload")}>{t("common.back")}</Button>
                <Button
                  variant="primary"
                  loading={busy}
                  onClick={() => review(applyMapping(table.columns, table.rows, mapping))}
                >
                  {t("common.continue")}
                </Button>
              </>
            }
          />
          <Table>
            <thead>
              <tr>
                <Th>{t("import.column")}</Th>
                <Th className="hidden sm:table-cell">{t("import.sample")}</Th>
                <Th>{t("import.field")}</Th>
              </tr>
            </thead>
            <tbody>
              {table.columns.map((column, i) => (
                <Tr key={column}>
                  <Td className="font-medium">
                    <span className="flex items-center gap-2">
                      <FileXlsIcon className="size-4 text-fg-subtle" />
                      {column}
                    </span>
                  </Td>
                  <Td className="hidden max-w-64 truncate text-fg-muted sm:table-cell">
                    {table.rows.find((r) => r[i])?.[i] ?? ""}
                  </Td>
                  <Td>
                    <Select
                      className="w-60"
                      value={mapping[column] ?? ""}
                      onChange={(e) =>
                        setMapping((m) => ({
                          ...m,
                          [column]: (e.target.value || null) as ImportTarget | null,
                        }))
                      }
                    >
                      <option value="">{t("import.ignore")}</option>
                      {IMPORT_TARGETS.map((target) => (
                        <option key={target} value={target}>
                          {targetLabel(t, target)}
                        </option>
                      ))}
                    </Select>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {step === "review" && (
        <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
          <Card className="min-w-0">
            <CardHeader
              title={t("import.reviewTitle", { count: preview.length })}
              description={
                <span className="mt-1 flex flex-wrap gap-1.5">
                  <Badge tone="success">{t("import.valid", { count: counts.valid })}</Badge>
                  {counts.invalid > 0 && (
                    <Badge tone="danger">{t("import.invalid", { count: counts.invalid })}</Badge>
                  )}
                  {counts.duplicates > 0 && (
                    <Badge tone="warning">{t("import.duplicates", { count: counts.duplicates })}</Badge>
                  )}
                </span>
              }
            />
            <div className="max-h-[60vh] divide-y divide-border overflow-y-auto">
              {preview.map((row) => (
                <div key={row.index} className="flex flex-col gap-2 px-5 py-3 sm:flex-row sm:items-center">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-[13px] font-medium">
                      {row.data ? (
                        <CheckCircleIcon className="size-4 text-success" />
                      ) : (
                        <WarningCircleIcon className="size-4 text-danger" />
                      )}
                      <span className="truncate">{row.displayName || "?"}</span>
                    </div>
                    <div className="pl-6 text-[12px] text-fg-muted">
                      {[row.data?.companyName, row.data?.email1].filter(Boolean).join(" · ")}
                    </div>
                    {row.errors.map((e) => (
                      <div key={e.field} className="pl-6 text-[12px] text-danger">
                        {t(`contacts.fields.${e.field}`, { defaultValue: e.field })}:{" "}
                        {t(`contacts.validation.${e.message}`, { defaultValue: e.message })}
                      </div>
                    ))}
                    {row.duplicates.slice(0, 1).map((d) => (
                      <div
                        key={d.contactId}
                        className="flex items-center gap-1 pl-6 text-[12px] text-warning"
                      >
                        <CopyIcon className="size-3" />
                        {t("import.duplicateOf", {
                          name: d.displayName,
                          reason: t(`import.reasons.${d.reason}`),
                        })}
                      </div>
                    ))}
                    {row.duplicateInFile !== null && (
                      <div className="pl-6 text-[12px] text-warning">{t("import.inFile")}</div>
                    )}
                  </div>
                  {row.data && (
                    <Select
                      className="w-48"
                      value={actions[row.index] ?? "create"}
                      onChange={(e) =>
                        setActions((a) => ({ ...a, [row.index]: e.target.value as RowAction }))
                      }
                    >
                      <option value="create">{t("import.actionCreate")}</option>
                      {row.duplicates.length > 0 && (
                        <option value="update">{t("import.actionUpdate")}</option>
                      )}
                      <option value="skip">{t("import.actionSkip")}</option>
                    </Select>
                  )}
                </div>
              ))}
            </div>
          </Card>
          <div className="flex flex-col gap-4">
            <Card className="p-5">
              <div className="text-[13.5px] font-semibold">{t("import.targetLists")}</div>
              <p className="mt-0.5 mb-3 text-[12.5px] text-fg-muted">{t("import.targetListsHint")}</p>
              <div className="flex flex-col gap-1">
                {editableLists.map((list) => (
                  <label
                    key={list.id}
                    className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-subtle"
                  >
                    <Checkbox
                      checked={targetLists.includes(list.id)}
                      onCheckedChange={(v) =>
                        setTargetLists((ids) => (v ? [...ids, list.id] : ids.filter((x) => x !== list.id)))
                      }
                      label={list.name}
                    />
                    <span
                      className="size-2 rounded-full"
                      style={{ background: LIST_COLOR_HEX[list.color] }}
                    />
                    <span className="text-[13px]">{list.name}</span>
                  </label>
                ))}
              </div>
            </Card>
            <Button
              variant="primary"
              size="lg"
              className="justify-center"
              loading={busy}
              disabled={!toImport.length}
              onClick={commit}
            >
              {resumable
                ? t(busy ? "import.saving" : "import.resume", {
                    done: formatNumber(resumable.done),
                    total: formatNumber(resumable.total),
                  })
                : t("import.submit", { count: toImport.length })}
            </Button>
            <Button onClick={() => setStep(table ? "map" : "upload")}>{t("common.back")}</Button>
          </div>
        </div>
      )}
    </Page>
  );
}
